import { CaptureButton } from "../components/CaptureButton";
import { CollectionCard } from "../components/CollectionCard";
import { EmptyState } from "../components/EmptyState";
import { ErrorState } from "../components/ErrorState";
import { CollectionCardSkeleton, LoadingAnnouncement } from "../components/Skeleton";
import { useCollections } from "../lib/queries";

/** The shelf: every volume Foundling has opened on your behalf. */
export function Collections() {
  const collections = useCollections();

  const pending = (collections.data ?? []).filter(
    (collection) => collection.quarryState === "pending",
  ).length;

  return (
    <div>
      <header className="mb-6 border-b-2 border-rule pb-4">
        <h1 className="display-2 text-[2.25rem] leading-none text-ink sm:text-5xl">
          Collections
        </h1>
        <p className="mt-2 max-w-prose font-prose leading-relaxed text-ink-soft">
          A volume opens itself the first time you find something of a kind in a place. Each one
          comes with a list of what else is worth looking for there.
        </p>
        {pending > 0 ? (
          <p className="mt-3 field-label-tight text-ochre">
            {pending === 1
              ? "One hunt list is still being drawn up"
              : `${pending} hunt lists are still being drawn up`}
          </p>
        ) : null}
      </header>

      {collections.isError ? (
        <ErrorState error={collections.error} onRetry={() => void collections.refetch()} />
      ) : null}

      {collections.isPending ? (
        <>
          <LoadingAnnouncement>Fetching your collections…</LoadingAnnouncement>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <CollectionCardSkeleton key={index} />
            ))}
          </div>
        </>
      ) : null}

      {collections.isSuccess && collections.data.length === 0 ? (
        <EmptyState
          title="No volumes on the shelf yet"
          motif="list"
          action={<CaptureButton variant="inline" label="Photograph something" />}
        >
          <p>
            Collections build themselves around your finds. Photograph a tree in Wisconsin and
            Foundling opens <span className="italic">Arboreal Wisconsin</span>, then suggests the
            other trees you might reasonably hope to come across.
          </p>
          <p className="mt-3">
            One thing to know: a find with no place can't join a collection. If a find is missing
            from here, it's almost certainly sitting in{" "}
            <span className="italic">Unfiled</span> waiting for a place.
          </p>
        </EmptyState>
      ) : null}

      {collections.isSuccess && collections.data.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {collections.data.map((collection) => (
            <CollectionCard key={collection.id} collection={collection} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
